/**
 * Spec 218 — ouderschaps-/geboorteverlof direct boeken namens een medewerker
 * (backoffice `/verlof/ouderschap-boeken`). Gedeeld request-schema + preview-DTO:
 * de API valideert ermee én het formulier valideert er client-side mee, zodat
 * het form niets kan sturen dat de API weigert.
 *
 * Drie manieren om dagen te kiezen (precies één):
 *  - periode: `vanDatum` (+ optioneel `totDatum`) — een losse dag of een
 *    aaneengesloten periode (weekend/feestdag/0-urendag overgeslagen);
 *  - losse dagen: `datums` — exact die dagen;
 *  - weekpatroon: `patroon` — vaste weekdag(en) binnen een periode, elke week
 *    of om de N weken ("elke woensdag", "om de week maandag + vrijdag").
 * Elke gekozen dag wordt een eigen DEFINITIEVE opname (los in te trekken).
 */
import { z } from 'zod'

export const OUDERSCHAP_BOEKBARE_TYPES = [
  'OUDERSCHAP_BETAALD',
  'OUDERSCHAP_ONBETAALD',
  'GEBOORTE',
  'GEBOORTE_AANVULLEND',
  'ZWANGERSCHAP',
  'ONBETAALD',
] as const
export type OuderschapBoekType = (typeof OUDERSCHAP_BOEKBARE_TYPES)[number]

export const OUDERSCHAP_BOEK_TYPE_LABELS: Record<OuderschapBoekType, string> = {
  OUDERSCHAP_BETAALD: 'Ouderschapsverlof - betaald deel',
  OUDERSCHAP_ONBETAALD: 'Ouderschapsverlof - onbetaald deel',
  GEBOORTE: 'Geboorteverlof (100%)',
  GEBOORTE_AANVULLEND: 'Aanvullend geboorteverlof (70%)',
  ZWANGERSCHAP: 'Zwangerschaps-/bevallingsverlof',
  ONBETAALD: 'Onbetaald verlof',
}

/** Typen die op een kind-emmer boeken (kind verplicht). */
export function isOuderschapBoekType(t: OuderschapBoekType): boolean {
  return t === 'OUDERSCHAP_BETAALD' || t === 'OUDERSCHAP_ONBETAALD'
}

/** Maximaal aantal dagen per boeking (één jaar). */
export const OUDERSCHAP_BOEK_MAX_DAGEN = 366

const datum = z.string().date('Ongeldige datum (jjjj-mm-dd)')

export const OuderschapBoekPatroonSchema = z
  .object({
    /** ISO-weekdagen: 1 = maandag … 7 = zondag. */
    weekdagen: z.array(z.number().int().min(1).max(7)).min(1, 'Kies minimaal één weekdag').max(7),
    vanDatum: datum,
    totDatum: datum,
    /** 1 = elke week, 2 = om de week, … (gerekend vanaf de week van `vanDatum`). */
    elkeNWeken: z.number().int().min(1).max(4).default(1),
  })
  .refine((p) => p.totDatum >= p.vanDatum, {
    message: 'De einddatum ligt vóór de begindatum',
    path: ['totDatum'],
  })

export const OuderschapBoekRequestSchema = z
  .object({
    employeeId: z.string().min(1, 'Kies een medewerker'),
    type: z.enum(OUDERSCHAP_BOEKBARE_TYPES),
    childId: z.string().min(1).optional(),
    vanDatum: datum.optional(),
    totDatum: datum.optional(),
    datums: z
      .array(datum)
      .min(1, 'Kies minimaal één dag')
      .max(OUDERSCHAP_BOEK_MAX_DAGEN, `Maximaal ${OUDERSCHAP_BOEK_MAX_DAGEN} dagen per boeking`)
      .optional(),
    patroon: OuderschapBoekPatroonSchema.optional(),
    /** Uren per dag; leeg = de roosteruren van die dag. */
    urenPerDag: z.number().positive().max(24).optional(),
    opmerking: z.string().max(2000).optional(),
  })
  .refine(
    (d) =>
      [d.vanDatum !== undefined, d.datums !== undefined, d.patroon !== undefined].filter(Boolean)
        .length === 1,
    {
      message: 'Kies één manier: een periode, losse dagen of een weekpatroon',
      path: ['vanDatum'],
    },
  )
  .refine((d) => d.totDatum === undefined || d.vanDatum !== undefined, {
    message: 'Een einddatum hoort bij een periode met begindatum',
    path: ['totDatum'],
  })
  .refine((d) => !d.vanDatum || !d.totDatum || d.totDatum >= d.vanDatum, {
    message: 'De einddatum ligt vóór de begindatum',
    path: ['totDatum'],
  })
  .refine((d) => !isOuderschapBoekType(d.type) || !!d.childId, {
    message: 'Kies een kind voor ouderschapsverlof',
    path: ['childId'],
  })

export type OuderschapBoekRequest = z.input<typeof OuderschapBoekRequestSchema>

/** Wat er met een kandidaat-dag gebeurt. Alleen BOEKEN telt mee in de totalen. */
export type OuderschapBoekDagStatus =
  | 'BOEKEN'
  | 'AL_GEBOEKT'
  | 'WEEKEND'
  | 'FEESTDAG'
  | 'GEEN_ROOSTERUREN'

export const OUDERSCHAP_BOEK_DAG_STATUS_LABELS: Record<OuderschapBoekDagStatus, string> = {
  BOEKEN: 'Wordt geboekt',
  AL_GEBOEKT: 'Al geboekt',
  WEEKEND: 'Weekend',
  FEESTDAG: 'Feestdag',
  GEEN_ROOSTERUREN: 'Geen roosteruren',
}

export interface OuderschapBoekDagDTO {
  /** yyyy-mm-dd */
  datum: string
  /** ISO-weekdag 1..7 */
  weekdag: number
  status: OuderschapBoekDagStatus
  /** Te boeken uren (0 als de dag niet geboekt wordt). */
  uren: number
  /** Roosteruren van die dag (ter vergelijking met een handmatige urenPerDag). */
  roosterUren: number
  /** Resterend saldo in de kind-emmer ná deze dag (null zonder emmer). */
  saldoNa: number | null
  /** Ander actief verlof op dezelfde dag (informatief, blokkeert niet). */
  anderVerlof: string | null
}

export interface OuderschapBoekPreviewDTO {
  ok: boolean
  aantalDagen: number
  totaalUren: number
  overgeslagen: number
  medewerker: string | null
  emmer: {
    toegekend: number
    resterendVoor: number
    resterendNa: number
    vervaltOp: string | null
  } | null
  waarschuwingen: string[]
  fouten: string[]
  dagen: OuderschapBoekDagDTO[]
}

/** Kind + ouderschaps-emmers zoals de verlof-schermen ze nodig hebben (AVG-minimaal: geen geboortedatum/notities). */
export interface OuderschapKindEmmerDTO {
  entitlementId: string
  toegekend: number
  opgenomen: number
  resterend: number
  /** yyyy-mm-dd */
  expiresAt: string | null
}

export interface OuderschapKindDTO {
  id: string
  naam: string
  betaald: OuderschapKindEmmerDTO | null
  onbetaald: OuderschapKindEmmerDTO | null
}
